import type { DashboardMode, PendingGroup, ProcessedCard } from '@/types'
import { FORMAT_DISPLAY_NAMES } from '@/utils/labels'
import { formatDateBR } from '@/utils/dateParser'

interface PendingListProps {
  groups: PendingGroup[]
  mode: DashboardMode
}

export function PendingList({ groups, mode }: PendingListProps) {
  if (groups.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-8 text-brand-taupe">
        <CheckCircleIcon />
        <p className="mt-2 text-sm font-medium">
          {mode === 'production'
            ? 'Todos os conteúdos estão finalizados ou agendados! 🎉'
            : 'Todos os conteúdos estão agendados! 🎉'}
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {groups.map((group) => (
        <PendingGroup key={group.label} group={group} mode={mode} />
      ))}
    </div>
  )
}

function PendingGroup({ group, mode: _mode }: { group: PendingGroup; mode: DashboardMode }) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-2">
        <span className="text-xs font-semibold text-brand-taupe uppercase tracking-wider">
          {group.displayName}
        </span>
        <span className="text-xs text-brand-taupe bg-brand-beige px-2 py-0.5 rounded-full">
          {group.cards.length}
        </span>
      </div>
      <div className="space-y-1.5">
        {group.cards.map((card) => (
          <CardRow key={card.id} card={card} />
        ))}
      </div>
    </div>
  )
}

function CardRow({ card }: { card: ProcessedCard }) {
  const formatLabel =
    card.formatLabels.length > 0
      ? FORMAT_DISPLAY_NAMES[card.formatLabels[0]]
      : null

  return (
    <a
      href={card.shortUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="flex items-center gap-3 p-3 rounded-xl border border-brand-beige
                 bg-white hover:bg-brand-beige-light hover:border-brand-beige
                 transition-all duration-150 group"
    >
      {/* Date chip */}
      <span className="shrink-0 text-xs font-mono text-brand-taupe bg-brand-beige
                       px-2 py-0.5 rounded-lg min-w-[72px] text-center">
        {formatDateBR(card.contentDate)}
      </span>

      {/* Name */}
      <span className="flex-1 text-sm text-brand-black truncate group-hover:text-brand-taupe transition-colors">
        {card.name}
      </span>

      {/* Format badge */}
      {formatLabel && (
        <span className="shrink-0 badge bg-brand-beige text-brand-taupe text-xs">
          {formatLabel}
        </span>
      )}

      {/* All label badges */}
      <div className="hidden sm:flex items-center gap-1 shrink-0">
        {card.rawLabels.slice(0, 3).map((label) => (
          <span
            key={label}
            className="badge bg-brand-beige-light text-brand-taupe border border-brand-beige text-xs"
          >
            {label}
          </span>
        ))}
        {card.rawLabels.length > 3 && (
          <span className="badge bg-brand-beige-light text-brand-taupe text-xs">
            +{card.rawLabels.length - 3}
          </span>
        )}
      </div>

      {/* External link icon */}
      <ExternalLinkIcon />
    </a>
  )
}

function CheckCircleIcon() {
  return (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <circle cx="12" cy="12" r="9" />
      <path d="M8.5 12l2.5 2.5 4.5-4.5" />
    </svg>
  )
}

function ExternalLinkIcon() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      className="text-brand-beige group-hover:text-brand-taupe transition-colors shrink-0"
    >
      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
      <path d="M15 3h6v6" />
      <path d="M10 14L21 3" />
    </svg>
  )
}
